import { CancelToken } from 'axios';
import { API_V1, API_V2 } from '@front-app/services/api';
import { parseApiSrc } from '@front-app/store/util/image';
import { atobUtf8 } from '@front-app/util/string';
import { imageFetcher } from '@front-app/util/image';
import {
	GroupConditions,
	Groups,
	GroupStats,
	GroupWithCommunity,
	PublicGroupData,
} from './GroupModel';
import { UserSearchItem } from '../UserApi/UserModel';

export function getCurrentUserGroups(): Promise<GroupWithCommunity[]> {
	return API_V1.get('/groups')
		.then(({ data }) => data as GroupWithCommunity[])
		.then(parseGroups);
}

export function getGroup(id: string): Promise<GroupWithCommunity> {
	return API_V1.get(`/groups/${id}?enableCommunity=true&trackUserView=false`)
		.then(({ data }) => data as GroupWithCommunity)
		.then(parseGroup);
}

export function getPublicGroup(id: string): Promise<PublicGroupData> {
	return API_V2.get(`/public/groups/${id}`)
		.then(({ data }) => data as PublicGroupData);
}

export function getGroupStats(groupIdList: string[]): Promise<GroupStats[]> {
	if (groupIdList.length === 0) {
		return Promise.resolve([]);
	}
	const params = groupIdList.reduce((urlSearchParams, id) => {
		urlSearchParams.append('groupIds', id);
		return urlSearchParams;
	}, new URLSearchParams());
	return API_V1.get('/groups/stats', { params })
		.then(({ data }) => data as GroupStats[]);
}

export const getGroupLogo = imageFetcher((id: string) => `/groups/${id}/logo`);
// Cachebust the banner since it should update regardless of browser cache
export const getGroupBanner = imageFetcher((id: string) => `/groups/${id}/banner?${Date.now()}=${Date.now()}`);

export interface GroupUserSearchParams {
	name: string;
	limit?: number;
}

// eslint-disable-next-line max-len
export const searchUsers = (groupId: string, params: GroupUserSearchParams, cancelToken?: CancelToken): Promise<UserSearchItem[]> => API_V1
	.get(`/group/${groupId}/members/search`, { params, cancelToken })
	.then(({ data }) => data as UserSearchItem[]);

export function parseGroup(group: GroupWithCommunity): GroupWithCommunity {
	const logo = parseApiSrc(group.logoSrc);
	const banner = parseApiSrc(group.bannerSrc);
	const bannerLink = atob(group.bannerLink).trim();
	const termsAndConditionsHtml = atobUtf8(group.termsAndConditionsHtml).trim();
	const pageHtml = atobUtf8(group.pageHtml).trim();
	const conditions = parseConditions(group.conditions);
	return {
		...group,
		conditions,
		logo,
		banner,
		bannerLink,
		pageHtml,
		termsAndConditionsHtml,
	};
}

function parseGroups(groups: Groups): Groups {
	return groups.map(parseGroup);
}

function toInfinity(val: number | undefined | null): number {
	return (val === undefined || val === null) ? Infinity : val;
}

function parseConditions(conditions: Partial<GroupConditions>): GroupConditions {
	return {
		maxMembers: toInfinity(conditions.maxMembers),
		maxModerators: toInfinity(conditions.maxModerators),
		maxAdministrators: toInfinity(conditions.maxAdministrators),
		maxUploadFileSize: toInfinity(conditions.maxUploadFileSize),
		maxTotalUploadFileSize: toInfinity(conditions.maxTotalUploadFileSize),
	};
}
